// @flow strict

import * as React from 'react';
import { Formik, type FormikHelpers } from 'formik';
import auth0 from 'auth0-js';
import { Button, FormGridRow, FormGridCell, Message } from '@ansarada/ace-react';
import styles from './styles.scss';

import { dataRoomUrl, apiUrl } from '../../../core/api/apiConfig';
import { raiseUserSubmitEvent } from '../../../core/lib/segment-actions';
import type { FormValue, FormValueWithUpflowy, JobTitleGroup, DataroomStatus } from './types';
import {
  INVALID_SESSION_ID_STATUS,
  MAX_WAITING_TIME_IN_SECONDS,
  CHECKING_STATUS_INTERVAL_TIME_IN_SECONDS,
} from './constants';
import { removeFreeTrialStorage } from '../helpers';
import { getValidateSchema } from './utils';
import type { ProvisioningData } from '../../FreeTrial/types';
import { profile, provisioning } from './api';
import UserProfileInputs from './UserProfileInputs';
import OnBoardingLayout from '../../FreeTrial/Internal/OnBoardingLayout';
import FooterTerms from './FooterTerms';

type Props = {
  provisioningData: ProvisioningData,
  spaClientId: string,
  authorizationServerDomain: string,
  userEmail: string,
  enabledUpflowy: boolean,
};

const initialFormValues: FormValue = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyName: '',
  jobTitleGroup: '',
  jobTitleId: '',
  jobTitleGroupOther: '',
  jobTitleOther: '',
  locationId: '',
  password: '',
  isSso: false,
};

const initialFormValuesWithUpflowy: FormValueWithUpflowy = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyName: '',
  locationId: '',
  password: '',
  isSso: false,
};

const maxNumberOfRetry = MAX_WAITING_TIME_IN_SECONDS / CHECKING_STATUS_INTERVAL_TIME_IN_SECONDS;

const UserProfileForm = ({
  provisioningData,
  spaClientId,
  authorizationServerDomain,
  userEmail,
  enabledUpflowy,
}: Props) => {
  const [jobTitleGroups, setJobTitleGroups] = React.useState<JobTitleGroup[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoggingin, setIsLoggingin] = React.useState(false);
  const [isVerifyingPassword, setIsVerifyingPassword] = React.useState(false);
  const [retry, setRetry] = React.useState(maxNumberOfRetry);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [dataroomStatus, setDataroomStatus] = React.useState<DataroomStatus>({
    dataRoomId: null,
    dataRoomUserId: null,
    dataRoomProjectName: null,
    status: null,
  });
  const [userProfile, setUserProfile] = React.useState<{ isCreated: boolean, values: FormValue }>({
    isCreated: false,
    values: { ...initialFormValues },
  });

  const goTo500ErrorPage = () => {
    window.location.href = new URL(`${apiUrl.ansaradadotcomBaseUrl}/500`);
  };

  React.useEffect(() => {
    (async function fetchJobTitle() {
      try {
        const data = await profile.getJobTitle();
        setJobTitleGroups(data.jobTitleGroups);
      } catch (error) {
        goTo500ErrorPage();
      }
    })();
  }, []);

  React.useEffect(() => {
    if (retry <= 0) goTo500ErrorPage();
  }, [retry]);

  React.useEffect(() => {
    if (dataroomStatus.status === 1 || dataroomStatus.status === 2) return undefined;

    const timer = setTimeout(async () => {
      try {
        const {
          ProvisioningStatus,
          DataRoomId,
          DataRoomUserId,
          DataRoomProjectName,
          Status,
        } = await provisioning.getStatus(provisioningData.sessionId);

        if (Status && Status === INVALID_SESSION_ID_STATUS) {
          setDataroomStatus(prev => ({ ...prev, status: 2 }));
        } else {
          setDataroomStatus({
            dataRoomId: DataRoomId,
            dataRoomUserId: DataRoomUserId,
            dataRoomProjectName: DataRoomProjectName,
            status: ProvisioningStatus,
          });
          if (isSubmitting) setRetry(prev => prev - 1);
        }
      } catch (error) {
        setDataroomStatus(prev => ({ ...prev, status: 2 }));
      }
    }, CHECKING_STATUS_INTERVAL_TIME_IN_SECONDS * 1000);

    return () => {
      if (timer) clearTimeout(timer);
    };
  });

  const autoLogin = (userPassword?: string) => {
    const auth0Client = new auth0.WebAuth({
      responseType: 'code',
      domain: authorizationServerDomain,
      clientID: spaClientId,
    });

    setIsVerifyingPassword(true);

    auth0Client.login(
      {
        username: userEmail,
        password: userPassword || userProfile.values.password,
        realm: 'DataRoom',
        redirectUri: `${window.location.origin}/free-trial/signup-callback`,
        audience: 'https://dataroom.ansarada.com/',
        scope: 'openid profile email',
        state: `${dataRoomUrl}/${dataroomStatus.dataRoomProjectName || ''}${
          enabledUpflowy ? '?dest=permalink' : ''
        }`,
        onRedirecting: function onLoginSuccess(done) {
          setIsVerifyingPassword(false);
          setIsLoggingin(true);
          done();
        },
      },
      function handleLoginError() {
        if (provisioningData.isProfileExists) {
          setLoginError('The password is incorrect');
          setIsVerifyingPassword(false);
        } else {
          goTo500ErrorPage();
        }
      },
    );
  };

  React.useEffect(() => {
    if (userProfile.isCreated) {
      if (dataroomStatus.status === 1) {
        setIsSubmitting(false);
        autoLogin();

        if (enabledUpflowy) {
          profile.completeSignup({
            SignupToken: provisioningData.signupToken,
            AssetType: 'DataRoom',
            AssetId: String(dataroomStatus.dataRoomId),
            AssetUserId: String(dataroomStatus.dataRoomUserId),
          });
        }
      } else if (dataroomStatus.status === 2) {
        goTo500ErrorPage();
      }
    }
  }, [dataroomStatus.status, userProfile.isCreated]);

  const handleProfileSubmit = async (values: FormValue, actions: FormikHelpers<FormValue>) => {
    try {
      setIsSubmitting(true);
      if (!provisioningData.isProfileExists) {
        const userData = enabledUpflowy
          ? {
              signupToken: provisioningData.signupToken,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: values.phoneNumber,
              companyName: values.companyName,
              locationId: Number(values.locationId),
              password: values.password,
            }
          : {
              signupToken: provisioningData.signupToken,
              firstName: values.firstName,
              lastName: values.lastName,
              phoneNumber: values.phoneNumber,
              companyName: values.companyName,
              jobTitleId: Number(values.jobTitleId),
              jobTitleOther: values.jobTitleOther,
              jobTitleGroupOther: values.jobTitleGroupOther,
              locationId: Number(values.locationId),
              password: values.password,
            };

        await profile.create(userData);
      }

      setUserProfile({ isCreated: true, values });
      removeFreeTrialStorage(provisioningData.sessionId);

      if (loginError) {
        setIsVerifyingPassword(false);
        setIsSubmitting(false);
        setLoginError(null);
        autoLogin(values.password);
      }
    } catch (error) {
      if (error.status === 400) {
        actions.setErrors(error.errors);
        setIsSubmitting(false);
      } else {
        goTo500ErrorPage();
      }
    }
  };

  const isWaiting = isSubmitting || isLoggingin || isVerifyingPassword;
  const message = !loginError ? 'Your room is ready, we are logging you in.' : loginError;
  const verifyingPasswordMessage = provisioningData.isProfileExists
    ? 'Verifying your account...'
    : 'Creating your profile...';

  return (
    <Formik
      initialValues={enabledUpflowy ? initialFormValuesWithUpflowy : initialFormValues}
      validationSchema={getValidateSchema(provisioningData.isProfileExists, enabledUpflowy)}
      onSubmit={handleProfileSubmit}
    >
      {({ handleSubmit, handleReset, submitForm, ...formikProps }) => (
        <OnBoardingLayout
          title="Complete your profile"
          actionButtons={[
            <Button
              onClick={() => {
                raiseUserSubmitEvent('Complete Profile Clicked');
                submitForm();
              }}
              loading={isWaiting}
              className={styles.submitBtn}
              key="next"
              variant="Primary"
              disabled={isWaiting}
              id="btn-complete-signup"
            >
              {provisioningData.isProfileExists ? 'Complete Signup' : 'Complete Profile'}
            </Button>,
          ]}
          footer={
            <FormGridRow>
              <FormGridCell>
                <FooterTerms />
              </FormGridCell>
            </FormGridRow>
          }
          bgMobile="picture"
          bgDesktop="picture"
        >
          <form onSubmit={handleSubmit} onReset={handleReset} autoComplete="off">
            <UserProfileInputs
              {...formikProps}
              isSubmitting={isWaiting}
              isProfileExists={provisioningData.isProfileExists}
              jobTitleGroups={jobTitleGroups}
              enabledUpflowy={enabledUpflowy}
            />
          </form>
          {isSubmitting && (
            <Message context="content" className={styles.messageInprogress}>
              We are preparing your room. Please wait...
            </Message>
          )}
          {isVerifyingPassword && (
            <Message context="content" className={styles.messageInprogress}>
              {verifyingPasswordMessage}
            </Message>
          )}
          {(isLoggingin || loginError) && (
            <Message
              context="content"
              className={loginError ? styles.messageError : styles.messageSuccess}
            >
              {message}
            </Message>
          )}
        </OnBoardingLayout>
      )}
    </Formik>
  );
};

export default UserProfileForm;
